import { useCallback, useRef } from "react";
import { useToast } from "src/hooks/useToast";
import useUserStore from "src/store/useUserStore";
import { getPDA1020_R01List } from "./PDA1020_R01Api";
import usePDA1020_R01Store from "./Store_PDA1020_R01";

export const usePDA1020_R01Content = () => {
  const userCompCd = useUserStore((state) => state.userInfo?.compCd);
  const startDate = usePDA1020_R01Store(
    (state) => state.PDA1020_R01Filter.startDate
  );
  const endDate = usePDA1020_R01Store(
    (state) => state.PDA1020_R01Filter.endDate
  );
  const isLoading = usePDA1020_R01Store((state) => state.isLoading);
  const setPDA1020_R01Filter = usePDA1020_R01Store(
    (state) => state.setPDA1020_R01Filter
  );
  const setPDA1020_R01List = usePDA1020_R01Store(
    (state) => state.setPDA1020_R01List
  );
  const setIsLoading = usePDA1020_R01Store(
    (state) => state.setIsLoading
  );
  const setScrollTop = usePDA1020_R01Store((state) => state.setScrollTop);
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  const latestRequestIdRef = useRef(0);

  showToastRef.current = showToast;

  const handleSearch = useCallback(async () => {
      if (!startDate || !endDate) {
        showToastRef.current("warning", "생산일자 기간을 선택하세요.");
        return;
      }
      if (startDate > endDate) {
        showToastRef.current(
          "warning",
          "생산일자 시작일은 종료일보다 늦을 수 없습니다."
        );
        return;
      }
      if (!userCompCd) {
        showToastRef.current(
          "error",
          "로그인 사용자의 사업장 정보를 확인할 수 없습니다."
        );
        return;
      }

      const requestId = ++latestRequestIdRef.current;
      setPDA1020_R01Filter({ compCd: userCompCd });
      setPDA1020_R01List([]);
      setScrollTop(null);
      setIsLoading(true);

      try {
        const response = await getPDA1020_R01List({
          compCd: userCompCd,
          startDate,
          endDate,
        });

        if (requestId !== latestRequestIdRef.current) return;

        const list = response?.data?.data;
        setPDA1020_R01List(Array.isArray(list) ? list : []);

        if (!Array.isArray(list) || list.length === 0) {
          showToastRef.current("warning", "조회된 생산실적이 없습니다.");
        }
      } catch (error: unknown) {
        if (requestId !== latestRequestIdRef.current) return;
        console.error("[PDA1020_R01] 생산실적 조회 실패:", error);
        setPDA1020_R01List([]);
        showToastRef.current("error", "생산실적 조회에 실패했습니다.");
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, [
      endDate,
      setIsLoading,
      setPDA1020_R01Filter,
      setPDA1020_R01List,
      setScrollTop,
      startDate,
      userCompCd,
    ]
  );

  const handleStartDateChange = useCallback(
    (nextStartDate: string) => {
      setPDA1020_R01Filter({ startDate: nextStartDate });
    },
    [setPDA1020_R01Filter]
  );

  const handleEndDateChange = useCallback(
    (nextEndDate: string) => {
      setPDA1020_R01Filter({ endDate: nextEndDate });
    },
    [setPDA1020_R01Filter]
  );

  return {
    startDate,
    endDate,
    isLoading,
    handleStartDateChange,
    handleEndDateChange,
    handleSearch,
  };
};
